import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthName = (n) => (n ? MONTH_NAMES[n - 1] : "—");

const PRIORITY_COLORS = {
  critical:  { bg: "#fee2e2", color: "#b91c1c" },
  urgent:    { bg: "#fff7ed", color: "#c2410c" },
  please_do: { bg: "#dbeafe", color: "#1d4ed8" },
};
const PRIORITY_LABELS = { critical: "Critical", urgent: "Urgent", please_do: "Please Do" };

function inspectionStatusBadge(status) {
  const map = {
    in_progress: { label: "In Progress", bg: "#dbeafe", color: "#1d4ed8" },
    passed:      { label: "Passed",      bg: "#dcfce7", color: "#15803d" },
    failed:      { label: "Failed",      bg: "#fee2e2", color: "#b91c1c" },
  };
  const s = map[status] || { label: status, bg: colors.lightGrey, color: colors.slate };
  return (
    <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "20px", backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function inspectionTypeLabel(type) {
  return { monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" }[type] || type;
}

function fmtDate(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

// Row renderers per panel type
function rowServiceRequest(req) {
  const pc = PRIORITY_COLORS[req.priority] || PRIORITY_COLORS.urgent;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "20px", backgroundColor: pc.bg, color: pc.color }}>
          {PRIORITY_LABELS[req.priority] || req.priority}
        </span>
        <span style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{req.site_name}</span>
      </div>
      <div style={{ fontSize: "12px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{req.service_request}</div>
      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "3px" }}>{fmtDate(req.date_requested)}</div>
    </div>
  );
}

function rowInspection(insp) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
        {inspectionStatusBadge(insp.status)}
        <span style={{ fontSize: "11px", fontWeight: "600", color: colors.slate }}>{inspectionTypeLabel(insp.inspection_type)}</span>
      </div>
      <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{insp.site_name}</div>
      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
        {fmtDate(insp.date)}{insp.assigned_to_name ? ` · ${insp.assigned_to_name}` : ""}
      </div>
    </div>
  );
}

function groupEFSMsBySite(efsms, monthField) {
  const groups = {};
  efsms.forEach(e => {
    if (!groups[e.site]) {
      groups[e.site] = {
        id: e.site,
        site_name: e.site_name,
        site_address: e.site_address,
        categories: [],
        scheduledMonth: e[monthField],
      };
    }
    if (e.category_name && !groups[e.site].categories.includes(e.category_name)) {
      groups[e.site].categories.push(e.category_name);
    }
  });
  return Object.values(groups);
}

function rowEFSM(grouped) {
  return (
    <div>
      <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{grouped.site_name}</div>
      <div style={{ fontSize: "12px", color: colors.slate }}>
        {grouped.categories.length > 0 ? grouped.categories.join(", ") : "—"}
      </div>
      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>Scheduled: {monthName(grouped.scheduledMonth)}</div>
    </div>
  );
}

function rowSite(site) {
  return (
    <div>
      <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{site.site_name}</div>
      <div style={{ fontSize: "12px", color: colors.slate }}>{site.site_address}</div>
    </div>
  );
}

function rowAccount(acc) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{acc.account_name}</div>
        <div style={{ fontSize: "12px", color: colors.slate, textTransform: "capitalize" }}>{acc.tier || "—"}</div>
      </div>
      {acc.annual_value > 0 && (
        <div style={{ fontSize: "13px", fontWeight: "600", color: colors.darkGrey }}>
          ${Number(acc.annual_value).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export function ManagementDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPanel, setSelectedPanel] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/sites/`),
      axios.get(`${API}/accounts/`),
      axios.get(`${API}/inspections/`),
      axios.get(`${API}/service-requests/`),
      axios.get(`${API}/site-efsms/`),
    ])
      .then(([sitesRes, accountsRes, inspRes, srRes, efsmsRes]) => {
        const now = new Date();
        const thisMonth = now.getMonth() + 1;
        const thisYear = now.getFullYear();
        const sites = sitesRes.data;
        const accounts = accountsRes.data;
        const inspections = inspRes.data;
        const serviceRequests = srRes.data;
        const efsms = efsmsRes.data;

        const activeSites = sites.filter((s) => s.status === "active");
        const activeAccounts = accounts.filter((a) => a.status === "active");
        const openRequests = serviceRequests.filter((r) => r.status === "incomplete");
        const thisMonthInspections = inspections.filter((i) => {
          const d = new Date(i.date);
          return d.getMonth() + 1 === thisMonth && d.getFullYear() === thisYear;
        });
        const failedInspections = inspections.filter((i) => i.status === "failed");
        const inProgress = inspections.filter((i) => i.status === "in_progress");

        const monthlyEfsms = efsms.filter((e) => e.is_monthly);
        const monthlyDueGrouped    = groupEFSMsBySite(monthlyEfsms.filter((e) => e.monthly_scheduled_month === thisMonth), "monthly_scheduled_month");
        const monthlyOverdueGrouped = groupEFSMsBySite(monthlyEfsms.filter((e) => e.monthly_scheduled_month && e.monthly_scheduled_month < thisMonth), "monthly_scheduled_month");

        const biAnnualEfsms = efsms.filter((e) => e.is_bi_annual);
        const biAnnualDueGrouped    = groupEFSMsBySite(biAnnualEfsms.filter((e) => e.bi_annual_scheduled_month === thisMonth), "bi_annual_scheduled_month");
        const biAnnualOverdueGrouped = groupEFSMsBySite(biAnnualEfsms.filter((e) => e.bi_annual_scheduled_month && e.bi_annual_scheduled_month < thisMonth), "bi_annual_scheduled_month");

        const annualEfsms = efsms.filter((e) => e.is_annual);
        const annualDueGrouped    = groupEFSMsBySite(annualEfsms.filter((e) => e.annual_scheduled_month === thisMonth), "annual_scheduled_month");
        const annualOverdueGrouped = groupEFSMsBySite(annualEfsms.filter((e) => e.annual_scheduled_month && e.annual_scheduled_month < thisMonth), "annual_scheduled_month");

        setData({
          activeSites: activeSites.length,
          activeAccounts: activeAccounts.length,
          openRequests: openRequests.length,
          failedInspections: failedInspections.length,
          inProgress: inProgress.length,
          thisMonthInspections: thisMonthInspections.length,
          monthlyDueThisMonth:    monthlyDueGrouped.length,
          monthlyOverdue:         monthlyOverdueGrouped.length,
          biAnnualDueThisMonth:   biAnnualDueGrouped.length,
          biAnnualOverdue:        biAnnualOverdueGrouped.length,
          annualStartingThisMonth: annualDueGrouped.length,
          annualDueThisMonth:     annualDueGrouped.length,
          annualOverdue:          annualOverdueGrouped.length,
          // panel lists
          openRequestsList: openRequests,
          inProgressList: inProgress,
          failedList: failedInspections,
          thisMonthList: thisMonthInspections,
          monthlyDueList:          monthlyDueGrouped,
          monthlyOverdueList:      monthlyOverdueGrouped,
          biAnnualDueList:         biAnnualDueGrouped,
          biAnnualOverdueList:     biAnnualOverdueGrouped,
          annualDueList:           annualDueGrouped,
          annualOverdueList:       annualOverdueGrouped,
          activeSitesList: activeSites,
          activeAccountsList: activeAccounts,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const openPanel = (title, list, renderRow) => {
    if (!list || list.length === 0) return;
    setSelectedPanel({ title, list, renderRow });
  };

  const KPICard = ({ label, value, color, subtext, urgent, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setHovered(!!onClick)}
        onMouseLeave={() => setHovered(false)}
        style={{
          backgroundColor: urgent && value > 0 ? "#fff8f8" : colors.white,
          borderRadius: "10px",
          padding: "20px",
          boxShadow: hovered ? "0 4px 12px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.08)",
          borderLeft: `4px solid ${color}`,
          cursor: onClick ? "pointer" : "default",
          transition: "box-shadow 0.15s",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          position: "relative",
        }}
      >
        <div style={{ fontSize: "32px", fontWeight: "700", color }}>{value}</div>
        <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{label}</div>
        {subtext && <div style={{ fontSize: "11px", color: "#999" }}>{subtext}</div>}
        {onClick && value > 0 && (
          <div style={{ position: "absolute", bottom: "12px", right: "14px", fontSize: "18px", color: "#ccc" }}>›</div>
        )}
      </div>
    );
  };

  const ComingSoonCard = ({ label }) => (
    <div style={{ backgroundColor: colors.white, borderRadius: "10px", padding: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: "4px solid #ddd", display: "flex", flexDirection: "column", gap: "6px", opacity: 0.6 }}>
      <div style={{ fontSize: "32px", fontWeight: "700", color: "#ccc" }}>—</div>
      <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{label}</div>
      <div style={{ fontSize: "11px", color: "#aaa" }}>Coming soon</div>
    </div>
  );

  const SectionHeader = ({ title, color }) => (
    <div style={{ gridColumn: "1 / -1", fontSize: "12px", fontWeight: "700", color: color || "#666", textTransform: "uppercase", letterSpacing: "1px", paddingTop: "8px", borderBottom: `2px solid ${color || "#eee"}`, paddingBottom: "6px", marginBottom: "4px" }}>
      {title}
    </div>
  );

  const now = new Date();
  const monthNameStr = now.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Management Dashboard</div>
        <div style={{ fontSize: "13px", color: "#999" }}>{monthNameStr}</div>
      </div>
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading dashboard...</div>
        ) : (
          <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
            <SectionHeader title="System Status" color={colors.red} />
            <ComingSoonCard label="Fire Systems Offline" />
            <ComingSoonCard label="Fire Panel Faults" />
            <KPICard
              label="Service Requests Open"
              value={data.openRequests}
              color={data.openRequests > 0 ? colors.orange : colors.green}
              urgent subtext="Awaiting action"
              onClick={data.openRequests > 0 ? () => openPanel("Service Requests Open", data.openRequestsList, rowServiceRequest) : undefined}
            />
            <KPICard
              label="Inspections In Progress"
              value={data.inProgress}
              color={colors.blue}
              subtext="Not yet completed"
              onClick={data.inProgress > 0 ? () => openPanel("Inspections In Progress", data.inProgressList, rowInspection) : undefined}
            />

            <SectionHeader title="Follow Ups & Rectifications" color={colors.red} />
            <KPICard
              label="Failed Inspections"
              value={data.failedInspections}
              color={data.failedInspections > 0 ? colors.red : colors.green}
              urgent subtext="Require rectification"
              onClick={data.failedInspections > 0 ? () => openPanel("Failed Inspections", data.failedList, rowInspection) : undefined}
            />
            <KPICard
              label="Rectifications"
              value={data.failedInspections}
              color={data.failedInspections > 0 ? colors.orange : colors.green}
              urgent subtext="Open defects from fails"
              onClick={data.failedInspections > 0 ? () => openPanel("Rectifications", data.failedList, rowInspection) : undefined}
            />
            <KPICard
              label="Follow Ups"
              value={data.openRequests}
              color={colors.orange}
              subtext="Customer requests pending"
              onClick={data.openRequests > 0 ? () => openPanel("Follow Ups", data.openRequestsList, rowServiceRequest) : undefined}
            />
            <KPICard
              label="Inspections This Month"
              value={data.thisMonthInspections}
              color={colors.blue}
              subtext="Completed this month"
              onClick={data.thisMonthInspections > 0 ? () => openPanel("Inspections This Month", data.thisMonthList, rowInspection) : undefined}
            />

            <SectionHeader title="Monthly Services" color={colors.blue} />
            <KPICard
              label="Due This Month"
              value={data.monthlyDueThisMonth}
              color={colors.blue}
              subtext="Scheduled for this month"
              onClick={data.monthlyDueThisMonth > 0 ? () => openPanel("Monthly Services — Due This Month", data.monthlyDueList, rowEFSM) : undefined}
            />
            <KPICard
              label="Overdue"
              value={data.monthlyOverdue}
              color={data.monthlyOverdue > 0 ? colors.red : colors.green}
              urgent subtext="Past scheduled month"
              onClick={data.monthlyOverdue > 0 ? () => openPanel("Monthly Services — Overdue", data.monthlyOverdueList, rowEFSM) : undefined}
            />
            <div /><div />

            <SectionHeader title="Bi-Annual Services" color={colors.blue} />
            <KPICard
              label="Due This Month"
              value={data.biAnnualDueThisMonth}
              color={colors.blue}
              subtext="Scheduled for this month"
              onClick={data.biAnnualDueThisMonth > 0 ? () => openPanel("Bi-Annual Services — Due This Month", data.biAnnualDueList, rowEFSM) : undefined}
            />
            <KPICard
              label="Overdue"
              value={data.biAnnualOverdue}
              color={data.biAnnualOverdue > 0 ? colors.red : colors.green}
              urgent subtext="Past scheduled month"
              onClick={data.biAnnualOverdue > 0 ? () => openPanel("Bi-Annual Services — Overdue", data.biAnnualOverdueList, rowEFSM) : undefined}
            />
            <div /><div />

            <SectionHeader title="Annual Services" color={colors.blue} />
            <KPICard
              label="Starting This Month"
              value={data.annualStartingThisMonth}
              color={colors.blue}
              subtext="Kick off this month"
              onClick={data.annualStartingThisMonth > 0 ? () => openPanel("Annual Services — Starting This Month", data.annualDueList, rowEFSM) : undefined}
            />
            <KPICard
              label="Due This Month"
              value={data.annualDueThisMonth}
              color={colors.orange}
              subtext="Must complete this month"
              onClick={data.annualDueThisMonth > 0 ? () => openPanel("Annual Services — Due This Month", data.annualDueList, rowEFSM) : undefined}
            />
            <KPICard
              label="Overdue"
              value={data.annualOverdue}
              color={data.annualOverdue > 0 ? colors.red : colors.green}
              urgent subtext="Past scheduled month"
              onClick={data.annualOverdue > 0 ? () => openPanel("Annual Services — Overdue", data.annualOverdueList, rowEFSM) : undefined}
            />
            <div />

            <SectionHeader title="Portfolio" color={colors.darkGrey} />
            <KPICard
              label="Active Sites"
              value={data.activeSites}
              color={colors.darkGrey}
              subtext="Under service contract"
              onClick={data.activeSites > 0 ? () => openPanel("Active Sites", data.activeSitesList, rowSite) : undefined}
            />
            <KPICard
              label="Active Accounts"
              value={data.activeAccounts}
              color={colors.darkGrey}
              subtext="Customer accounts"
              onClick={data.activeAccounts > 0 ? () => openPanel("Active Accounts", data.activeAccountsList, rowAccount) : undefined}
            />
            <div /><div />
          </div>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedPanel && (
        <>
          <div
            onClick={() => setSelectedPanel(null)}
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.15)", zIndex: 99 }}
          />
          <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: "420px", backgroundColor: colors.white, zIndex: 100, boxShadow: "-4px 0 24px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div style={{ fontWeight: "700", fontSize: "15px", color: colors.text }}>{selectedPanel.title}</div>
              <button onClick={() => setSelectedPanel(null)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, fontSize: "18px", lineHeight: 1, padding: "2px" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {selectedPanel.list.map((item, i) => (
                <div key={item.id || i} style={{ padding: "12px 20px", borderBottom: `1px solid ${colors.border}` }}>
                  {selectedPanel.renderRow(item)}
                </div>
              ))}
            </div>
            <div style={{ padding: "10px 20px", fontSize: "13px", color: "#94a3b8", borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
              {selectedPanel.list.length} item{selectedPanel.list.length !== 1 ? "s" : ""}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
